/**
 * Browse step through the deck's unhandled suggestions.
 *
 * Returns the id of the next (direction 1) or previous (direction -1)
 * suggestion that is not in passedIds, relative to activeId, wrapping at both
 * ends of the queue. Identity is by id, never by index: the caller's queue
 * order is the only order.
 *
 * - activeId passed or missing: steps from activeId's position in the queue
 *   (a missing id steps from just before the start, or just past the end).
 * - activeId is the only unhandled suggestion: returns activeId.
 * - nothing unhandled: returns null.
 */
export function stepSuggestion(
  queue: ReadonlyArray<{ id: string }>,
  passedIds: ReadonlySet<string>,
  activeId: string | null,
  direction: 1 | -1
): string | null {
  const n = queue.length;
  if (n === 0) return null;

  const found = activeId == null ? -1 : queue.findIndex((s) => s.id === activeId);
  // A missing id behaves as if it sat just outside the queue in the direction
  // of travel's origin, so a forward step lands on the first unhandled entry
  // and a backward step on the last.
  const start = found >= 0 ? found : direction === 1 ? -1 : n;

  for (let offset = 1; offset <= n; offset += 1) {
    const index = (((start + direction * offset) % n) + n) % n;
    const candidate = queue[index];
    if (!passedIds.has(candidate.id)) return candidate.id;
  }
  return null;
}
